import api from "./api";
import { generateErrorMessage } from "../utils/message";


/*Systems*/
export const registerSystem = async (systemData) => {
  try {
    const response = await api.post("/system", systemData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("register", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


export const fetchAllSystemsFromAPI = async () => {
  try {
    const response = await api.get("/system/all/page");

    return response.data.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const fetchSystemsFromAPI = async (page = 1, perPage = 10, search = "", columnFilters = {}, sort = {}) => {
  try {
    // Build params object with column-specific filters
    const params = {
      page,
      per_page: perPage
    };

    // Add search parameter if provided (for backward compatibility)
    if (search) {
      params.search = search;
    }

    // Add column-specific parameters
    if (columnFilters.config_type) params.config_type = columnFilters.config_type;
    if (columnFilters.config_key) params.config_key = columnFilters.config_key;
    if (columnFilters.config_value) params.config_value = columnFilters.config_value;
    if (columnFilters.description) params.description = columnFilters.description;

    if (sort && sort.sortBy) {
      params.sort_by = sort.sortBy;
      params.sort_dir = sort.sortDir === 'desc' ? 'desc' : 'asc';
    }

    console.log('🌐 SystemService - fetchSystemsFromAPI called with params:', params);

    const response = await api.get("/system", {
      params: params,
    });

    console.log('🌐 SystemService - API response received:', response.data);

    return response.data.data;
  } catch (error) {
    console.log('🌐 SystemService - API error:', error);
    const fallback = generateErrorMessage("get data", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* Fetch every config matching the filters, walking server pages (for Export). */
export const fetchAllSystemsForExport = async ({ search = "", columnFilters = {}, sort = {} } = {}, onProgress) => {
  const PER_PAGE = 1000;
  const collected = [];
  let page = 1;
  let total = 0;
  for (;;) {
    const response = await fetchSystemsFromAPI(page, PER_PAGE, search, columnFilters, sort);
    const payload = response?.configs || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    collected.push(...rows);
    total = Number(payload.total) || collected.length;
    if (onProgress) onProgress(collected.length, total);
    const lastPage = Number(payload.last_page) || 1;
    if (rows.length === 0 || page >= lastPage || collected.length >= total) break;
    page += 1;
  }
  return collected;
};

export const deleteSystemsFromAPI = async (config_key, config_type) => {
  try {
    console.log("deleteSystemsFromAPI called with:", { config_key, config_type });
    console.log("API URL will be:", `/system/${config_key}/${config_type}`);
    const response = await api.delete(`/system/${config_key}/${config_type}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("delete data", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};


export const fetchSystemsById = async (config_key,config_type) => {
  try {    
    const response = await api.get(`/system/${config_key}/${config_type}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data system", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const updateSystem = async (id, systemData) => {
  try {
    const response = await api.put(`/system`, systemData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("update", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Upload System Excel File */
export const uploadSystemFileToAPI = async (formData, onUploadProgress) => {
  try {
    const response = await api.post("/system/submit-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // Reports how much of the FILE has reached the server (0-100). Server-side
      // row inserting happens after this hits 100 and is not tracked here.
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (event.total) {
              onUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
          }
        : undefined,
    });
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("upload system file", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Get Commodity Options */
export const fetchCommodityOptions = async () => {
  try {
    const response = await api.get("/system/commodity");
    return response.data.data.configs;
  } catch (error) {
    const fallback = generateErrorMessage("get commodity options", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Get Plant Options */
export const fetchPlantOptions = async () => {
  try {
    const response = await api.get("/system/plant");
    return response.data.data.configs;
  } catch (error) {
    const fallback = generateErrorMessage("get plant options", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Get Auto Refresh Config */
export const fetchAutoRefreshConfig = async (config_key) => {
  try {
    // Route: /system/{key}/{type}
    // For auto refresh: config_type = 'AUTO_REFRESH', config_key = 'DASHBOARD' or 'MONITORING'
    const response = await api.get(`/system/${config_key}/AUTO_REFRESH`);
    
    // Response structure: { status: 'success', data: { config: { config_value: '...', ... } } }
    if (response.data?.status === 'success' && response.data?.data?.config?.config_value) {
      return response.data.data.config.config_value;
    }
    return null;
  } catch (error) {
    // If config not found, return null (will use default value)
    console.warn(`Auto refresh config not found for ${config_key}, using default value`);
    return null;
  }
};

/* 📌 Get All Config Types */
export const fetchConfigTypes = async () => {
  try {
    const response = await api.get("/system/config-types");
    return response.data.data.config_types || [];
  } catch (error) {
    const fallback = generateErrorMessage("get config types", "system");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* 📌 Get Update Stock Config */
export const fetchUpdateStockConfig = async () => {
  try {
    const configKeys = [
      'DAY_SHIFT_TIMES_FROM',
      'DAY_SHIFT_TIMES_TO',
      'NIGHT_SHIFT_TIMES_FROM',
      'NIGHT_SHIFT_TIMES_TO',
      'TIMING_ALLOWANCE',
      'STANDARD_UPDATE_HOURS'
    ];
    
    const configs = {};
    
    for (const key of configKeys) {
      try {
        const response = await api.get(`/system/${key}/STOCK_TIMING`);
        if (response.data?.status === 'success' && response.data?.data?.config?.config_value) {
          configs[key] = response.data.data.config.config_value;
        }
      } catch (error) {
        // Config not found, will use default value
        console.warn(`Update stock config not found for ${key}, using default value`);
      }
    }
    
    // Build arrays from FROM/TO values for backward compatibility
    if (configs.DAY_SHIFT_TIMES_FROM && configs.DAY_SHIFT_TIMES_TO) {
      configs.DAY_SHIFT_TIMES = [configs.DAY_SHIFT_TIMES_FROM, configs.DAY_SHIFT_TIMES_TO];
    }
    if (configs.NIGHT_SHIFT_TIMES_FROM && configs.NIGHT_SHIFT_TIMES_TO) {
      configs.NIGHT_SHIFT_TIMES = [configs.NIGHT_SHIFT_TIMES_FROM, configs.NIGHT_SHIFT_TIMES_TO];
    }
    
    return configs;
  } catch (error) {
    console.warn('Error fetching update stock config, using default values');
    return {};
  }
};


